import { ref, computed } from 'vue'
import { createBroadcast, confirmBroadcast, listMyBroadcasts, type CpBroadcast } from '@/services/wootdashBroadcasts'
import { onBroadcast } from '@/composables/useConversationRealtime'
import type { PendingBroadcast, CreateBroadcastInput } from '@/types/broadcast'

// Avisos em massa entre agentes (tecla K) no WootDash — substitui o broadcastService do n8n.
// Sem polling: os pendentes vêm uma vez no login (GET /broadcasts/minhas) e os novos chegam
// pelo evento SignalR "NovoBroadcast" (grupo do agente no ConversationHub).
// Estado de módulo (singleton): App.vue controla start/stop; o compositor só envia.
const pendingQueue = ref<PendingBroadcast[]>([])
const acking       = ref(false)
const sending      = ref(false)
const sendError    = ref('')

let token = ''
let unsubscribe: (() => void) | null = null

const IMAGE_URL = /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i

/** O WootDash guarda texto + uma mídia: mídia de imagem vira a imagem do aviso; outra, link. */
function toPending(b: { id: string; senderId: string; body: string; mediaUrl: string | null; createdAt: string; kind?: string }): PendingBroadcast {
  return {
    id: b.id,
    senderId: b.senderId,
    kind: b.kind ?? 'aviso',
    body: b.body,
    imageUrl: b.mediaUrl && IMAGE_URL.test(b.mediaUrl) ? b.mediaUrl : null,
    youtubeUrl: null,
    linkUrl: b.mediaUrl && !IMAGE_URL.test(b.mediaUrl) ? b.mediaUrl : null,
    createdAt: Math.floor(new Date(b.createdAt).getTime() / 1000),
  }
}

function enqueue(b: PendingBroadcast) {
  if (!pendingQueue.value.some(p => p.id === b.id)) pendingQueue.value = [...pendingQueue.value, b]
}

export function useBroadcast() {
  // Só o primeiro da fila é exibido por vez; o próximo aparece quando o atual é confirmado.
  const currentBroadcast = computed<PendingBroadcast | null>(() => pendingQueue.value[0] ?? null)

  async function ack(broadcastId: string) {
    if (!token || acking.value) return
    acking.value = true
    try {
      await confirmBroadcast(token, broadcastId)
      pendingQueue.value = pendingQueue.value.filter(b => b.id !== broadcastId)
    } catch (err) {
      console.warn('[Broadcast] ack:', err)
    } finally {
      acking.value = false
    }
  }

  /** Link e YouTube vão no texto (o WootDash guarda uma mídia só, que fica pra imagem). */
  async function sendBroadcast(input: CreateBroadcastInput) {
    sendError.value = ''
    sending.value = true
    try {
      const extras = [input.youtubeUrl, input.linkUrl].filter(Boolean)
      const body = extras.length ? `${input.body}\n\n${extras.join('\n')}` : input.body
      const result = await createBroadcast(token, body, input.imageUrl || null, input.recipientIds)
      if (!result.ok) throw new Error(result.status === 403 ? 'Sem permissão pra enviar aviso.' : `Erro ${result.status}`)
    } catch (err) {
      console.warn('[Broadcast] sendBroadcast:', err)
      sendError.value = err instanceof Error && err.message.startsWith('Sem permissão') ? err.message : 'Não foi possível enviar o aviso.'
      throw err
    } finally {
      sending.value = false
    }
  }

  /** Chamado por App.vue no login: carrega os pendentes e passa a ouvir os novos. */
  async function start(wootdashToken: string) {
    token = wootdashToken
    unsubscribe?.()
    unsubscribe = onBroadcast(evt => enqueue(toPending({ id: evt.broadcastId, senderId: evt.senderId, body: evt.body, mediaUrl: evt.mediaUrl, createdAt: evt.createdAt, kind: evt.kind })))
    const mine: CpBroadcast[] = await listMyBroadcasts(token)
    mine.filter(b => !b.confirmedAt).map(toPending).forEach(enqueue)
  }

  /** No logout: para de ouvir e limpa a fila (um agente B na mesma aba não vê o aviso de A). */
  function stop() {
    unsubscribe?.()
    unsubscribe = null
    token = ''
    pendingQueue.value = []
  }

  return { pendingQueue, currentBroadcast, acking, sending, sendError, ack, sendBroadcast, start, stop }
}
